// Vertalingen: Nederlands en Engels. Alle teksten die de speler ziet staan in nl.ts en en.ts;
// de rest van de code vraagt ze op met t('sleutel') of tn('sleutel', aantal).
// DOM-vrij, zodat ook de spelregels (src/game/*.ts) en hun tests het kunnen gebruiken.
import { nl, type MessageKey } from './nl';
import { en } from './en';

export type Language = 'nl' | 'en';
/** De keuze in de instellingen: 'auto' volgt de taal van het apparaat. */
export type LanguageSetting = 'auto' | Language;
export type { MessageKey };

export const LANGUAGES: readonly Language[] = ['nl', 'en'];

const DICTS: Record<Language, Record<string, string>> = { nl, en };

let current: Language = 'nl';
const listeners = new Set<(lang: Language) => void>();

/** Kiest de taal bij 'auto': Nederlands voor nl-apparaten (nl, nl-NL, nl-BE), anders Engels. */
export function resolveLanguage(setting: LanguageSetting | string | undefined, deviceLanguages: readonly string[] = []): Language {
  if (setting === 'nl' || setting === 'en') return setting;
  const first = deviceLanguages.find((l) => typeof l === 'string' && l);
  return first?.toLowerCase().startsWith('nl') ? 'nl' : first ? 'en' : 'nl';
}

export const getLanguage = (): Language => current;

export function setLanguage(lang: Language): void {
  if (lang === current) return;
  current = lang;
  for (const fn of listeners) fn(lang);
}

/** Meldt zich aan voor taalwissels; geeft een functie terug om je weer af te melden. */
export function onLanguageChange(fn: (lang: Language) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export type Params = Record<string, string | number>;

const fill = (text: string, params?: Params) =>
  params ? text.replace(/\{(\w+)\}/g, (m, k: string) => (k in params ? String(params[k]) : m)) : text;

/** Een tekst in de huidige taal. {naam} wordt vervangen door params.naam. Een ontbrekende sleutel valt terug op het Nederlands. */
export function t(key: MessageKey, params?: Params): string {
  return fill(DICTS[current][key] ?? nl[key] ?? key, params);
}

/** Enkelvoud of meervoud: zoekt `sleutel_one` bij precies 1, anders `sleutel_other`. {n} is het aantal. */
export function tn(key: string, n: number, params?: Params): string {
  const k = `${key}_${n === 1 ? 'one' : 'other'}` as MessageKey;
  return t(k, { n, ...params });
}

/** Getal met het decimaalteken van de taal: 4,5 in het Nederlands, 4.5 in het Engels. */
export function fmtNum(n: number, digits = 1): string {
  const text = n.toFixed(digits);
  return current === 'nl' ? text.replace('.', ',') : text;
}

/** Aantal sleutels, voor de pariteitstest. */
export const dictionaries = DICTS;
